import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight, CheckCircle2, Globe, Zap, Box, ShoppingBag,
  Workflow, Brain, LineChart, Sparkles, ExternalLink,
  TrendingUp, Layers, Code, Award, Users, Target
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ContactForm from '../components/ContactForm';
import SEO from '../components/SEO';

const Home = () => {
  const { language } = useLanguage();

  // Service Pillars
  const services = [
    {
      icon: ShoppingBag,
      titleTR: 'E-Ticaret & Marketplace',
      titleEN: 'E-Commerce & Marketplace',
      descTR: 'Amazon, Etsy, Shopify entegrasyonları ve multi-channel satış sistemleri',
      descEN: 'Amazon, Etsy, Shopify integrations and multi-channel sales systems',
      outcome: language === 'tr' ? 'Satışları %300 artırın' : 'Increase sales 300%',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Globe,
      titleTR: 'Web & Uygulama Geliştirme',
      titleEN: 'Web & App Development',
      descTR: 'React, Next.js, React Native ile enterprise-grade özel yazılım',
      descEN: 'Enterprise-grade custom software with React, Next.js, React Native',
      outcome: language === 'tr' ? '60% daha hızlı pazara' : '60% faster to market',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Workflow,
      titleTR: 'Otomasyon & Entegrasyon',
      titleEN: 'Automation & Integration',
      descTR: 'n8n ile iş süreçlerini otomatikleştirin, API entegrasyonları',
      descEN: 'Automate workflows with n8n, seamless API integrations',
      outcome: language === 'tr' ? 'Manuel işleri %80 azaltın' : 'Reduce manual work 80%',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Brain,
      titleTR: 'AI Ajanları & Chatbotlar',
      titleEN: 'AI Agents & Chatbots',
      descTR: 'GPT-5, Claude ile özel AI asistanları ve akıllı müşteri desteği',
      descEN: 'Custom AI assistants and intelligent support with GPT-5, Claude',
      outcome: language === 'tr' ? 'Destek maliyeti 50% düşer' : 'Cut support costs 50%',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Box,
      titleTR: '3D Modelleme & Baskı',
      titleEN: '3D Modeling & Printing',
      descTR: 'Ürün prototipleri, görselleştirme ve endüstriyel 3D baskı',
      descEN: 'Product prototypes, visualization and industrial 3D printing',
      outcome: language === 'tr' ? 'Prototip %75 hızlanır' : 'Prototyping 75% faster',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: LineChart,
      titleTR: 'Performans Pazarlama',
      titleEN: 'Performance Marketing',
      descTR: 'Google, Meta reklamları ve veri odaklı büyüme stratejileri',
      descEN: 'Google, Meta ads and data-driven growth strategies',
      outcome: 'ROAS 4x-8x',
      color: 'from-indigo-500 to-purple-500'
    }
  ];

  // Process Steps
  const process = [
    { step: '01', titleTR: 'Keşif', titleEN: 'Discovery', icon: Target },
    { step: '02', titleTR: 'Mimari', titleEN: 'Architecture', icon: Layers },
    { step: '03', titleTR: 'Geliştirme', titleEN: 'Development', icon: Code },
    { step: '04', titleTR: 'Entegrasyon', titleEN: 'Integration', icon: Workflow },
    { step: '05', titleTR: 'Optimizasyon', titleEN: 'Optimization', icon: TrendingUp }
  ];

  // Case Studies
  const caseStudies = [
    {
      titleTR: 'E-Ticaret Otomasyonu',
      titleEN: 'E-Commerce Automation',
      metricTR: '%85 Süreç Azalması',
      metricEN: '85% Faster Processing',
      tags: ['Amazon', 'n8n', 'API'],
      image: 'https://images.unsplash.com/photo-1661956602116-aa6865609028?w=800&h=600&fit=crop'
    },
    {
      titleTR: 'AI Müşteri Destek Sistemi',
      titleEN: 'AI Support System',
      metricTR: '%70 Otomasyon Oranı',
      metricEN: '70% Automated',
      tags: ['GPT-5', 'React', 'NLP'],
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=600&fit=crop'
    },
    {
      titleTR: 'Multi-Store Yönetim',
      titleEN: 'Multi-Store Management',
      metricTR: '%240 Büyüme',
      metricEN: '240% Growth',
      tags: ['Shopify', 'React Native'],
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=600&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title={language === 'tr' ? 'Global Reklam & Dijital Pazarlama Ajansı' : 'Global Advertising & Digital Marketing Agency'}
        description={language === 'tr'
          ? 'Cplus Media LLC; Google & Meta Reklamları, SEO, Web Yazılım, Sosyal Medya Yönetimi ve E-Ticaret Danışmanlığı ile markanızı büyütün.'
          : 'Cplus Media LLC helps you scale with Google & Meta Ads, SEO, Web Development, Social Media Management and E-Commerce Consulting.'}
        keywords="reklam ajansı, dijital pazarlama, google ads, seo, web tasarım, e-ticaret, sosyal medya yönetimi"
      />


      {/* Hero Section - Full Width Visual Centerpiece */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Overlay gradient for better text readability */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/40 to-white dark:from-gray-950/60 dark:via-gray-950/40 dark:to-gray-950"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-100 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-full backdrop-blur-sm"
              >
                <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  {language === 'tr' ? 'Enterprise AI & Otomasyon Çözümleri' : 'Enterprise AI & Automation Solutions'}
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-5xl lg:text-7xl font-bold text-gray-900 dark:text-white leading-[1.1] tracking-tight"
              >
                {language === 'tr' ? (
                  <>
                    İşinizi <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Otomasyon</span> ve <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">AI</span> ile Ölçeklendirin
                  </>
                ) : (
                  <>
                    Scale with <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Automation</span> & <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">AI</span>
                  </>
                )}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed"
              >
                {language === 'tr'
                  ? 'Amazon, Shopify entegrasyonları, AI ajanları ve n8n otomasyonları ile operasyonel verimliliği %80 artırıyoruz.'
                  : 'Boost operational efficiency 80% with Amazon, Shopify integrations, AI agents and n8n automation.'}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4 pt-4"
              >
                <a
                  href="/contact"
                  className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-2xl hover:shadow-blue-500/30 dark:hover:shadow-blue-500/50 transition-all font-semibold text-lg"
                >
                  {language === 'tr' ? 'Ücretsiz Danışma' : 'Free Consultation'}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href="/references"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white dark:bg-gray-800/50 border-2 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl hover:border-blue-600 dark:hover:border-blue-500 backdrop-blur-sm transition-all font-semibold text-lg shadow-sm hover:shadow-md"
                >
                  {language === 'tr' ? 'Referansları İncele' : 'View Case Studies'}
                  <ExternalLink className="ml-2 w-5 h-5" />
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="flex items-center gap-8 pt-6 border-t border-gray-200 dark:border-gray-800"
              >
                <div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">2005</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{language === 'tr' ? 'Kuruluş' : 'Founded'}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">350+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{language === 'tr' ? 'Proje' : 'Projects'}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">24/7</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{language === 'tr' ? 'Destek' : 'Support'}</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right: Visual Centerpiece - Premium Dashboard Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative hidden lg:block"
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 dark:from-blue-500/30 dark:to-purple-500/30 rounded-3xl blur-3xl"></div>

              {/* Main dashboard */}
              <div className="relative bg-white/80 dark:bg-gradient-to-br dark:from-gray-900/95 dark:to-gray-800/95 backdrop-blur-2xl border border-gray-200/50 dark:border-gray-700/50 rounded-3xl p-8 shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl shadow-lg"></div>
                    <div>
                      <div className="h-3 w-32 bg-gray-300 dark:bg-gray-700 rounded"></div>
                      <div className="h-2 w-20 bg-gray-200 dark:bg-gray-800 rounded mt-2"></div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg shadow-green-500/50"></div>
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-gray-400 dark:bg-gray-600 rounded-full"></div>
                  </div>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[
                    { value: 85, label: 'Automation', color: 'from-blue-500 to-cyan-500' },
                    { value: 92, label: 'Efficiency', color: 'from-purple-500 to-pink-500' },
                    { value: 78, label: 'AI Score', color: 'from-orange-500 to-red-500' },
                    { value: 95, label: 'Uptime', color: 'from-green-500 to-emerald-500' }
                  ].map((stat, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700/50"
                    >
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{stat.label}</div>
                      <div className="flex items-end space-x-2">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}%</div>
                        <TrendingUp className="w-4 h-4 text-green-500 mb-1" />
                      </div>
                      <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${stat.value}%` }}
                          transition={{ delay: 0.8 + i * 0.1, duration: 1 }}
                          className={`h-full bg-gradient-to-r ${stat.color}`}
                        ></motion.div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Chart */}
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700/50">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Performance</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Last 7 days</span>
                  </div>
                  <div className="h-32 flex items-end justify-between space-x-1.5">
                    {[40, 65, 45, 80, 60, 90, 75].map((height, i) => (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        animate={{ height: `${height}%` }}
                        transition={{ delay: 1 + i * 0.1, duration: 0.5 }}
                        className="flex-1 bg-gradient-to-t from-blue-600 to-purple-600 rounded-t-lg"
                      ></motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating elements */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl shadow-2xl shadow-blue-500/50 dark:shadow-blue-500/30 flex items-center justify-center"
              >
                <Brain className="w-12 h-12 text-white" />
              </motion.div>

              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-2xl shadow-purple-500/50 dark:shadow-purple-500/30 flex items-center justify-center"
              >
                <Workflow className="w-10 h-10 text-white" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Continue in next part... */}

      {/* Services Section - Card Grid with Staggered Animation */}
      <section className="py-32 bg-gray-50 dark:bg-gray-900 border-y border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {language === 'tr' ? 'Çözümlerimiz' : 'Our Solutions'}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              {language === 'tr'
                ? 'Enterprise sistemler, AI entegrasyonları ve otomasyon çözümleri'
                : 'Enterprise systems, AI integrations and automation solutions'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                  className="group relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl blur-xl -z-10"
                    style={{ background: `linear-gradient(to bottom right, var(--tw-gradient-stops))` }}
                  />
                  <div className="relative p-8 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl hover:border-transparent dark:hover:border-transparent shadow-sm hover:shadow-2xl transition-all">
                    <div className={`w-14 h-14 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      {language === 'tr' ? service.titleTR : service.titleEN}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 leading-relaxed">
                      {language === 'tr' ? service.descTR : service.descEN}
                    </p>
                    <div className="flex items-center text-sm font-semibold">
                      <CheckCircle2 className={`w-4 h-4 mr-2 bg-gradient-to-r ${service.color} bg-clip-text text-transparent`} />
                      <span className={`bg-gradient-to-r ${service.color} bg-clip-text text-transparent`}>
                        {service.outcome}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section - Alternating Layout */}
      <section className="py-32 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {language === 'tr' ? 'Çalışma Sürecimiz' : 'Our Process'}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              {language === 'tr' ? 'Kanıtlanmış metodoloji ile hızlı sonuçlar' : 'Fast results with proven methodology'}
            </p>
          </motion.div>

          <div className="relative">
            {/* Connection line */}
            <div className="absolute top-12 left-8 right-8 h-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 hidden lg:block"></div>

            <div className="grid md:grid-cols-5 gap-8 relative">
              {process.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.15 }}
                    className="relative"
                  >
                    <div className="text-center">
                      <div className="relative inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl text-2xl font-bold text-white mb-4 shadow-2xl shadow-blue-500/30 dark:shadow-blue-500/50">
                        <div className="absolute inset-0 bg-white dark:bg-gray-950 m-1 rounded-xl flex items-center justify-center">
                          <Icon className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                        </div>
                        <span className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center text-sm">
                          {step.step}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                        {language === 'tr' ? step.titleTR : step.titleEN}
                      </h3>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies - Full Width with Overlays */}
      <section className="py-32 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-between items-end mb-12"
          >
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-2">
                {language === 'tr' ? 'Başarı Hikayeleri' : 'Success Stories'}
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                {language === 'tr' ? 'Gerçek sonuçlar, gerçek müşteriler' : 'Real results, real clients'}
              </p>
            </div>
            <a
              href="/references"
              className="hidden md:inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold group"
            >
              {language === 'tr' ? 'Tümünü Gör' : 'View All'}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {caseStudies.map((study, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -8 }}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-2xl mb-4 shadow-lg">
                  <img
                    src={study.image}
                    alt={language === 'tr' ? study.titleTR : study.titleEN}
                    className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {study.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="text-xs bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-white border border-white/30"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                      {language === 'tr' ? study.titleTR : study.titleEN}
                    </h3>
                    <div className="flex items-center text-lg font-bold text-blue-400">
                      <TrendingUp className="w-5 h-5 mr-2" />
                      {language === 'tr' ? study.metricTR : study.metricEN}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Premium Form */}
      <section className="py-32 bg-white dark:bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl blur-3xl"></div>
            <div className="relative bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-3xl p-12 shadow-2xl">
              <div className="text-center mb-10">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                  {language === 'tr' ? 'Projenizi Başlatalım' : "Let's Build Your System"}
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-400">
                  {language === 'tr' ? 'Ücretsiz danışma için formu doldurun' : 'Fill the form for free consultation'}
                </p>
              </div>

              <ContactForm className="max-w-xl mx-auto" />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
